import { useCallback, useState } from "react"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { processQuery, parseReceipt, fetchChatSessions, fetchChatHistory, deleteChatSession, submitPaymentMethod } from "store/aiassistant"
import { cappedToaster } from "components/common/Toast"

function newSessionId() {
  return `s_${Math.floor(Math.random() * 100000)}`
}

function mapSessions(raw, defaultTitle) {
  return raw.map((s) => ({
    sessionId: s.session_id,
    title: s.title || defaultTitle,
    timestamp: s.last_activity_at ? new Date(s.last_activity_at).getTime() : 0
  }))
}

// Past exchanges only get a time if the service sent one -- showing "now" against a
// conversation from last week would be worse than showing nothing.
//
// The chat-history API (plugins/chat_history_manager.py) sends a SEPARATE
// timestamp per role -- user_created_at and assistant_created_at -- since a
// reply can land seconds (or, on a retried/self-corrected query, much
// longer) after the question that prompted it. This used to read
// exchange.timestamp / exchange.created_at, which the API never sends, so
// every message loaded from history rendered with no time at all.
function toTimestamp(raw) {
  const parsed = raw ? new Date(raw).getTime() : Number.NaN
  return Number.isNaN(parsed) ? null : parsed
}

function mapHistoryExchanges(raw) {
  return raw.flatMap((exchange, i) => [
    { id: `h-${i}-u`, role: "user", content: exchange.user, createdAt: toTimestamp(exchange.user_created_at) },
    {
      id: `h-${i}-a`, role: "ai", content: exchange.assistant, options: exchange.options || [],
      createdAt: toTimestamp(exchange.assistant_created_at)
    }
  ])
}

export function useChatSession() {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [sessionId, setSessionId] = useState(null)
  const [chatHistory, setChatHistory] = useState([])
  const [sessions, setSessions] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)
  const [deletingSessionId, setDeletingSessionId] = useState(null)

  const startNewSession = useCallback(() => {
    setSessionId(newSessionId())
    setChatHistory([])
  }, [])

  const sendMessage = useCallback(
    async (query, receiptFile) => {
      const filePreview = receiptFile ? URL.createObjectURL(receiptFile) : null
      setChatHistory((prev) => [...prev, { id: crypto.randomUUID(), role: "user", content: query, filePreview, createdAt: Date.now() }])
      setIsLoading(true)
      try {
        let receiptMetadata = null
        if (receiptFile) {
          const parsed = await dispatch(parseReceipt(receiptFile))
          receiptMetadata = parsed?.data ?? null
        }
        const data = await dispatch(processQuery(sessionId, query, receiptMetadata))
        if (data.session_id) setSessionId(data.session_id)
        setChatHistory((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: "ai",
            content: data.details || "",
            options: data.options || [],
            fileUpload: !!data.file_upload,
            addCard: data.add_card || null,
            createdAt: Date.now()
          }
        ])
      } catch {
        setChatHistory((prev) => [
          ...prev,
          { id: crypto.randomUUID(), role: "ai", content: t("ai_assistant.error_generic"), options: [], createdAt: Date.now() }
        ])
      } finally {
        setIsLoading(false)
      }
    },
    [dispatch, sessionId, t]
  )

  const submitCard = useCallback(
    async (addCard, paymentMethodId) => {
      setIsLoading(true)
      try {
        // Attach the card first; this throws (skips to catch) on any non-2xx response,
        // so the "card added" follow-up only fires once the payment-method API succeeds.
        const cardResult = await dispatch(submitPaymentMethod(addCard, paymentMethodId))
        if (cardResult?.session_id) setSessionId(cardResult.session_id)

        const data = await dispatch(processQuery(cardResult?.session_id || addCard?.session_id || sessionId, "card added"))
        if (data.session_id) setSessionId(data.session_id)
        setChatHistory((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: "ai",
            content: data.details || "",
            options: data.options || [],
            fileUpload: !!data.file_upload,
            addCard: data.add_card || null,
            createdAt: Date.now()
          }
        ])
      } catch {
        setChatHistory((prev) => [
          ...prev,
          { id: crypto.randomUUID(), role: "ai", content: t("ai_assistant.error_card"), options: [], createdAt: Date.now() }
        ])
      } finally {
        setIsLoading(false)
      }
    },
    [dispatch, sessionId, t]
  )

  const loadSessions = useCallback(async () => {
    setIsLoadingHistory(true)
    try {
      const data = await dispatch(fetchChatSessions())
      const list = Array.isArray(data) ? data : data?.sessions ?? []
      setSessions(mapSessions(list, t("ai_assistant.new_conversation")))
    } catch {
      setSessions([])
    } finally {
      setIsLoadingHistory(false)
    }
  }, [dispatch, t])

  const loadSession = useCallback(
    async (session) => {
      setSessionId(session.sessionId)
      setChatHistory([])
      setIsLoading(true)
      try {
        const data = await dispatch(fetchChatHistory(session.sessionId))
        if (Array.isArray(data)) setChatHistory(mapHistoryExchanges(data))
      } catch {
        setChatHistory([])
      } finally {
        setIsLoading(false)
      }
    },
    [dispatch]
  )

  const markOptionSelected = useCallback((msgId, selectedOption) => {
    setChatHistory((prev) => prev.map((m) => m.id === msgId ? { ...m, optionSelected: selectedOption } : m))
  }, [])

  const deleteSession = useCallback(
    async (sid, currentSessionId, onNewSession) => {
      setDeletingSessionId(sid)
      try {
        await dispatch(deleteChatSession(sid))
        setSessions((prev) => prev.filter((s) => s.sessionId !== sid))
        if (sid === currentSessionId) onNewSession()
        cappedToaster("delete-chat-session-success", "success", t("ai_assistant.session_deleted"))
      } catch {
        cappedToaster("delete-chat-session-error", "error", t("ai_assistant.session_delete_failed"))
      } finally {
        setDeletingSessionId(null)
      }
    },
    [dispatch, t]
  )

  return {
    sessionId,
    chatHistory,
    sessions,
    isLoading,
    isLoadingHistory,
    deletingSessionId,
    startNewSession,
    sendMessage,
    submitCard,
    loadSessions,
    loadSession,
    deleteSession,
    markOptionSelected
  }
}
